import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAllFuturePlannedVisits, PlannedVisit } from "../../api/reportsApi";
import { useNavigationBadgesStore, NavigationBadge } from "../..";
import { useEffect } from "react";
import { downloadCSV } from "../../utils/exportUtils";
import { getPlannedVisitPeople } from "../../utils/plannedVisitUtils";

const fmt = (iso: string | undefined) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pl-PL");
};

const fmtRange = (start: string, end?: string) => {
  if (!end) return fmt(start);
  const s = new Date(start);
  const e = new Date(end);
  if (s.toDateString() === e.toDateString()) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
};

const daysFromNow = (iso: string) => {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / 86_400_000);
};

export function FuturePlannedVisitsPage() {
  const navigate = useNavigate();
  const setItems = useNavigationBadgesStore((state: any) => state.setItems);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setItems([{ text: "Planowane wizyty", url: "" } as NavigationBadge]);
  }, [setItems]);

  const { data, isLoading } = useQuery<PlannedVisit[]>(
    ["all-future-planned-visits"],
    fetchAllFuturePlannedVisits,
    { refetchOnWindowFocus: false }
  );

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    if (!q) return data;
    return data.filter(
      (v) =>
        v.studyName?.toLowerCase().includes(q) ||
        v.resortName?.toLowerCase().includes(q) ||
        v.name?.toLowerCase().includes(q) ||
        getPlannedVisitPeople(v).some((p) => p.toLowerCase().includes(q))
    );
  }, [data, search]);

  const labelCls =
    "block text-[11px] font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em]";

  const handleExport = () => {
    const rows: (string | number | null)[][] = [
      ["Data start", "Data end", "Badanie", "Ośrodek", "Nazwa / typ", "Monitorzy", "Tryb", "Za (dni)"],
      ...filtered.map(v => [
        fmt(v.dateStart),
        v.dateEnd ? fmt(v.dateEnd) : "",
        v.studyName ?? "",
        v.resortName ?? "",
        v.name ?? "",
        getPlannedVisitPeople(v).join(", "),
        v.isRemote ? "zdalna" : "stacjonarna",
        daysFromNow(v.dateStart),
      ]),
    ];
    downloadCSV(rows, `Planowane_wizyty_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-[18px] font-semibold text-[var(--sc-text)]">
          Planowane wizyty
        </h1>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Szukaj (badanie, ośrodek, nazwa, e-mail)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm bg-[var(--sc-surface)] text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[#F4A832] w-72"
          />
          <button
            onClick={handleExport}
            disabled={filtered.length === 0}
            className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium bg-[var(--sc-text)] text-[var(--sc-surface)] hover:opacity-80 disabled:opacity-40 transition-colors whitespace-nowrap"
          >
            ↓ Eksportuj CSV
          </button>
        </div>
      </div>

      {isLoading && (
        <p className="text-sm text-[var(--sc-muted)]">Ładowanie…</p>
      )}

      {!isLoading && filtered.length === 0 && (
        <p className="text-sm text-[var(--sc-muted)]">
          Brak zaplanowanych wizyt w ciągu najbliższych 2 lat.
        </p>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className="rounded-[12px] border border-[var(--sc-border)] bg-[var(--sc-surface)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--sc-border)] bg-[var(--sc-bg)]">
                <th className="text-left px-4 py-3">
                  <span className={labelCls}>Data</span>
                </th>
                <th className="text-left px-4 py-3">
                  <span className={labelCls}>Badanie</span>
                </th>
                <th className="text-left px-4 py-3">
                  <span className={labelCls}>Ośrodek</span>
                </th>
                <th className="text-left px-4 py-3">
                  <span className={labelCls}>Nazwa / typ</span>
                </th>
                <th className="text-left px-4 py-3">
                  <span className={labelCls}>Monitorzy</span>
                </th>
                <th className="text-left px-4 py-3">
                  <span className={labelCls}>Tryb</span>
                </th>
                <th className="text-right px-4 py-3">
                  <span className={labelCls}>Za (dni)</span>
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((visit, i) => {
                const days = daysFromNow(visit.dateStart);
                const soon = days <= 14;
                const verySoon = days <= 3;
                return (
                  <tr
                    key={visit.id}
                    className={`border-b border-[var(--sc-border)] last:border-0 ${
                      i % 2 === 0 ? "" : "bg-[var(--sc-bg)]/40"
                    }`}
                  >
                    <td className="px-4 py-3 font-medium text-[var(--sc-text)] whitespace-nowrap">
                      {fmtRange(visit.dateStart, visit.dateEnd)}
                    </td>
                    <td className="px-4 py-3 text-[var(--sc-text)]">
                      {visit.studyName || "—"}
                    </td>
                    <td className="px-4 py-3 text-[var(--sc-text)]">
                      {visit.resortName || "—"}
                    </td>
                    <td className="px-4 py-3 text-[var(--sc-muted)]">
                      {visit.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-[var(--sc-muted)] text-xs">
                      {getPlannedVisitPeople(visit).map((p) => p.split("@")[0]).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3">
                      {visit.isRemote ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#EEF4FF] text-[#4A7CC7]">
                          zdalna
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--sc-bg)] text-[var(--sc-muted)]">
                          stacjonarna
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          verySoon
                            ? "bg-[#FDECEA] text-[#C03030]"
                            : soon
                            ? "bg-[#FFF8ED] text-[#C47A0A]"
                            : "text-[var(--sc-muted)]"
                        }`}
                      >
                        {days}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => navigate(`/visit/${visit.id}`)}
                        className="text-xs px-3 py-1.5 rounded-full bg-[#F4A832] text-[#0F0F0F] font-medium hover:bg-[#e09620] transition-colors"
                      >
                        Otwórz
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
